import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { AlertTriangle, CheckCircle2, Clock, GitBranch, LayoutDashboard } from 'lucide-react';
import {
  fetcher,
  Project,
  QualityGateSummary,
  SearchResult,
} from '../lib/api';
import { Badge, Card, EmptyState, ErrorState, LoadingCard, PageHeader } from '../components/ui';

const PROJECTS_KEY = '/api/v1/projects';
const TOP_ISSUES_KEY = '/api/v1/search/issues?size=5&from=0';

export function Home() {
  const { data: projects, isLoading, error } = useSWR<Project[]>(PROJECTS_KEY, fetcher);
  const { data: searchResult } = useSWR<SearchResult>(TOP_ISSUES_KEY, fetcher);

  const sorted = [...(projects ?? [])].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );
  const topProjects = sorted.slice(0, 5);
  const projectCount = projects?.length ?? 0;
  const issueTotal = searchResult?.total ?? 0;

  let analysesValue = '…';
  if (!isLoading) {
    analysesValue = sorted.length > 0 ? 'Active' : 'None yet';
  }

  let projectsContent;
  if (isLoading) {
    projectsContent = <LoadingCard label="Loading projects…" />;
  } else if (error) {
    projectsContent = <ErrorState error={error} />;
  } else if (topProjects.length === 0) {
    projectsContent = (
      <EmptyState
        icon={<GitBranch className="h-6 w-6" aria-hidden="true" />}
        title="No projects yet."
        description="Create a project and run the scanner CLI to see results here."
        action={
          <Link
            to="/projects"
            className="orb-badge bg-accent/10 text-accent border border-accent/30 hover:bg-accent/20 transition-colors"
          >
            Go to Projects
          </Link>
        }
      />
    );
  } else {
    projectsContent = (
      <Card as="ul" className="divide-y divide-border">
        {topProjects.map((p) => (
          <ProjectRow key={p.id} project={p} />
        ))}
      </Card>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-8 w-full space-y-8">
      <PageHeader
        eyebrow="orbital · lint"
        icon={<LayoutDashboard />}
        title="Dashboard"
        description="Code quality, self-hosted."
      />

      {/* Stats row — shown even while projects are loading */}
      {!error && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <StatCard
            label="Projects"
            value={isLoading ? '…' : String(projectCount)}
          />
          <StatCard
            label="Open issues"
            value={searchResult ? String(issueTotal) : '…'}
          />
          <StatCard
            label="Analyses"
            value={analysesValue}
          />
        </div>
      )}

      {/* Recent projects */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-base font-semibold text-fg">
            Recent Projects
          </h2>
          {projectCount > 5 && (
            <Link
              to="/projects"
              className="text-xs text-accent hover:underline"
            >
              View all {projectCount} →
            </Link>
          )}
        </div>

        {projectsContent}
      </section>

      {/* Top issues */}
      {searchResult && searchResult.hits.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base font-semibold text-fg">
              Recent Issues
            </h2>
            <Link
              to="/issues"
              className="text-xs text-accent hover:underline"
            >
              Browse all →
            </Link>
          </div>
          <Card as="ul" className="divide-y divide-border">
            {searchResult.hits.map((hit) => (
              <li key={hit.id} className="px-5 py-3 flex items-start gap-3">
                <SeverityDot severity={hit.severity} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-fg truncate">{hit.message}</p>
                  <p className="text-xs text-fg-muted truncate mt-0.5">
                    {hit.file_path}:{hit.line_start}
                  </p>
                </div>
                <span className="orb-badge bg-bg-inset text-fg-subtle border border-border shrink-0">
                  {hit.severity}
                </span>
              </li>
            ))}
          </Card>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-project row — self-fetches quality gate status.
// ---------------------------------------------------------------------------

function ProjectRow({ project }: { readonly project: Project }) {
  const { data: gate } = useSWR<QualityGateSummary>(
    `/api/v1/projects/${project.id}/quality-gate`,
    fetcher,
  );

  return (
    <li>
      <Link
        to={`/projects/${project.id}`}
        className="flex items-center gap-4 px-5 py-4 hover:bg-bg-hover transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-sm font-semibold text-fg truncate">
              {project.name}
            </span>
            <span className="orb-code text-xs">{project.slug}</span>
          </div>
          {project.repo_url ? (
            <div className="text-xs text-fg-subtle truncate mt-0.5">
              {project.repo_url}
            </div>
          ) : null}
        </div>
        <div
          className="flex items-center gap-1 text-xs text-fg-muted shrink-0"
          title="Default branch"
        >
          <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
          {project.default_branch}
        </div>
        <GateBadge status={gate?.gate_status} />
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Gate status badge
// ---------------------------------------------------------------------------

function GateBadge({ status }: { readonly status?: 'pass' | 'fail' | 'not_run' }) {
  if (!status || status === 'not_run') {
    return (
      <Badge tone="neutral" className="shrink-0 flex items-center gap-1">
        <Clock className="h-3 w-3" aria-hidden="true" />
        Not run
      </Badge>
    );
  }
  if (status === 'pass') {
    return (
      <Badge tone="success" className="shrink-0 flex items-center gap-1">
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        Passed
      </Badge>
    );
  }
  return (
    <Badge tone="danger" className="shrink-0 flex items-center gap-1">
      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      Failed
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

function StatCard({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <Card className="p-5">
      <p className="text-xs text-fg-muted mb-1">{label}</p>
      <p className="font-display text-2xl font-bold text-fg">{value}</p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Severity dot
// ---------------------------------------------------------------------------

function SeverityDot({ severity }: { readonly severity: string }) {
  const colours: Record<string, string> = {
    BLOCKER: 'bg-danger',
    CRITICAL: 'bg-danger/70',
    MAJOR: 'bg-warning',
    MINOR: 'bg-fg-muted',
    INFO: 'bg-fg-subtle',
  };
  const cls = colours[severity] ?? 'bg-fg-subtle';
  return (
    <span
      className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${cls}`}
      aria-label={severity}
    />
  );
}

